// @flow

import React, {Component} from 'react';
import Link from '@react/react-spectrum/Link';
import ModalTrigger from '@react/react-spectrum/ModalTrigger';
import Dialog from '@react/react-spectrum/Dialog';
import PlanModalContent from '../PlanModalContent/PlanModalContent';
import PlanModalContentScss from '../PlanModalContent/PlanModalContent.scss';
import CompatibilityUtil from '../../../../utils/CompatibilityUtil';
import DeviceDetectionUtil from '../../../../utils/DeviceDetectionUtil';
import AppsUtil from '../../../../utils/AppsUtil';
import AppPickerUtil from '../../../../utils/AppPicker/AppPickerUtil';
import HelpPopoverUtil from '../../../../utils/AppPicker/HelpPopoverUtil';
import {
	TAGS,
	DOWNLOAD_PAGE_FOOTER_FAQS,
	DOWNLOAD_PAGE_FOOTER_HELPFUL_LINKS,
	GET_HELP_OPEN_SOURCES,
	DOWNLOAD_CONTROLLER_USER_SOURCE
} from '../../../../constants/AppPicker/appPickerConstants';
import AppPickerAnalytics from '../../../../analytics/AppPickerAnalytics';
import DownloadPageFooterScss from './DownloadPageFooter.scss';
import HomeViewScss from './../HomeView/HomeView.scss';
import uuid from 'uuid';

const ANALYTICS = {
	FOOTER_LINK_CLICK: 'footer-link-click'
};

const FOOTER_LINKS_TYPE = {
	FAQ: 'faq',
	RESOURCES: 'helpful-links'
};

type Props = {
	sapCode?: string, /* This will not be provided if the footer is used from IncompatibleOsView */
	source: string, /* Place from where the Footer is being used */
	environment: string,
	formatMessage: Function,
	viewYourDesktopAppsLinkClicked?: Function,
	hasThor: ?boolean
};

type State = {
	faqListIsAvailable: boolean/* will be set once faq questions are available*/
}

export default class DownloadPageFooter extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		HelpPopoverUtil.setFaqListIsAvailable = this.setFaqListIsAvailable;
		this.state = {
			faqListIsAvailable: false
		};
	}

	setFaqListIsAvailable = () => {
		this.setState({faqListIsAvailable : true});
	}

	componentDidMount() {
		/* Disable right click -> open in new tab for these links */
		const linkElems = document.getElementsByClassName(DownloadPageFooterScss.link);
		if (linkElems.length > 0) {
			[...linkElems].forEach((link) => {
				link.removeAttribute('href');
			});
		}
	}

	openGetHelpWithSelectedQuestion(questionId: string) {
		let pageType;
		let compatibilityTags;
		const {sapCode, hasThor, source} = this.props;
		if (source === DOWNLOAD_CONTROLLER_USER_SOURCE.SINGLE_APP_POST_PURCHASE_PAGE) {
			pageType = [TAGS.PAGE.SINGLE_APP_PLAN];
			compatibilityTags = HelpPopoverUtil.getCompatibilityTags([sapCode]);
		}
		else {
			pageType = [TAGS.PAGE.APP_PICKER];
			compatibilityTags = HelpPopoverUtil.getCompatibilityTags([], true);
		}
		const updatingList = {
			pageType,
			installWorkflowTags: HelpPopoverUtil.getInstallWorkflowTag(hasThor),
			compatibilityTags
		};
		HelpPopoverUtil.updateHelpPopoverConfig(questionId, [updatingList]);
		HelpPopoverUtil.openGetHelpPopover(GET_HELP_OPEN_SOURCES.FOOTER_LINK_CLICK);
	}

	handleAnalyticsEvent(eventType: string, params: Object) {
		const analytics = new AppPickerAnalytics(this.props.environment);
		analytics.initAnalytics();
		let eventObj: {
			event?: {
				type?: string,
				subtype?: string,
				subcategory?: string
			},
			content?: {
				name?: string
			},
			ui?: {
				view_type?: ?string
			}
		} = {
			event: {}
		};
		switch (eventType) {
			case ANALYTICS.FOOTER_LINK_CLICK:
				eventObj = {
					event: {
						type: 'click',
						subtype: params.linkType,
						subcategory: (this.props.source === DOWNLOAD_CONTROLLER_USER_SOURCE.SINGLE_APP_POST_PURCHASE_PAGE) ? 'Download' : 'Welcome',
						guid: params && params.guid
					},
					content: {
						name: params.linkId,
						action: params && params.url
					}
				};
				break;
		}
		eventObj.ui = {view_type: analytics.getPageTypeString()}; // eslint-disable-line camelcase
		analytics.genericAnalyticEvent(eventObj);
	}

	openChat() {
		window.AdobeMessagingExperienceClient && window.AdobeMessagingExperienceClient.openMessagingWindow({sourceType: 'link'});
	}

	shouldShowViewYourDekstopAppsAffordance() {
		return !(this.props.source === DOWNLOAD_CONTROLLER_USER_SOURCE.SINGLE_APP_POST_PURCHASE_PAGE);
	}

	redirectToCreativeCloudWeb = (target) => {
		const guid = uuid.v4();
		const url = AppsUtil.openExternalLink(`${window.location.origin}/apps/all/desktop`, undefined, guid, undefined, undefined, target);
		return {
			guid,
			url
		};
	}

	onLinkClick = (e: Object, link: Object, type: string) => {
		let guid;
		let url;
		if (type === FOOTER_LINKS_TYPE.FAQ) {
			this.openGetHelpWithSelectedQuestion(link.id);
		}
		else {
			switch (link.id) {
				case 'view-your-plan':
					break;
				case 'view-desktop-apps':
					if (DeviceDetectionUtil.isLinux()) {
						const guidUrl = this.redirectToCreativeCloudWeb('_self');
						guid = guidUrl.guid;
						url = guidUrl.url;
					}
					else if (DeviceDetectionUtil.isChromeOS()) {
						const guidUrl = this.redirectToCreativeCloudWeb();
						guid = guidUrl.guid;
						url = guidUrl.url;
					}
					else {
						this.props.viewYourDesktopAppsLinkClicked && this.props.viewYourDesktopAppsLinkClicked();
					}
					break;
				case 'chat-with-support':
					this.openChat();
					break;
			}
		}
		this.handleAnalyticsEvent(ANALYTICS.FOOTER_LINK_CLICK, {linkId: link.id, linkType: type, guid, url});
	};

	getFaqList = () => {
		const {sapCode, hasThor} = this.props;
		let setNum = 'set1';
		if (sapCode && (CompatibilityUtil.isAppSupported(sapCode) || CompatibilityUtil.isAnyOlderVersionSupported(sapCode))) {
			if (['APRO', 'SPRK'].includes(sapCode) && !hasThor) {
				setNum = 'set2';
			}
			else {
				setNum = 'set1';
			}
		}
		else {
			if (DeviceDetectionUtil.isWin()) {
				setNum = 'set4';
			}
			else {
				setNum = 'set3';
			}
		}
		if (DeviceDetectionUtil.isLinux()) {
			setNum = 'set6';
		}
		if (DeviceDetectionUtil.isChromeOS()) {
			setNum = 'set5';
		}
		let faqList = Object.assign([], DOWNLOAD_PAGE_FOOTER_FAQS[setNum]);
		faqList = faqList.map((faqId) => {
			const getHelpDataItem = HelpPopoverUtil.fullQuestionList
				&& HelpPopoverUtil.fullQuestionList.find(item => item.id === faqId);
			return {
				text: getHelpDataItem && getHelpDataItem.questionText,
				id: faqId
			};
		});
		return faqList;
	}

	renderFooterList = (listData: Array<Object>, type: string) => {
		const footerList = [];
		listData.forEach((link) => {
			if ((link.text || link.textId)) {
				const linkElem = (
					<Link
						className={DownloadPageFooterScss.link}
						onClick={(e) => {this.onLinkClick(e, link ,type);}}
						onKeyPress={(e) => {
							if (e.keyCode === 13 || e.which === 13 || e.key === 'Enter') {
								this.onLinkClick(e, link, type);
							}
						}}
						variant='quiet'
					>
						<div tabIndex='0'>
							{link.text ? link.text : this.props.formatMessage({id: link.textId})}
						</div>
					</Link>
				);
				if (type === FOOTER_LINKS_TYPE.RESOURCES && link.id === 'view-your-plan') {
					footerList.push(this.renderViewYourPlanAffordance(linkElem));
				}
				else if (type === FOOTER_LINKS_TYPE.RESOURCES && link.id === 'view-desktop-apps') {
					if (this.shouldShowViewYourDekstopAppsAffordance()) {
						footerList.push(linkElem);
					}
				}
				else {
					footerList.push(linkElem);
				}
			}
		});
		return footerList;
	}

	shouldOpenModal = (e: Object) => {
		if (e.key === 'Enter' || e.keyCode === 13 || e.which === 13) {
			document.getElementById('plan-details-modal').click();
		}
	}

	renderViewYourPlanAffordance(linkElem: Object) {
		if (!AppPickerUtil.getViewYourPlanModalContent()) {
			return null;
		}
		return (
			<div className={`${HomeViewScss.viewYourPlan} ${DownloadPageFooterScss.viewYourPlan}`}>
				<ModalTrigger
					id='plan-details-modal'
					onKeyDown={(e) => {this.shouldOpenModal(e);}}>
					{linkElem}
					<Dialog
						className={PlanModalContentScss.viewYourPlanModal}
						confirmLabel={this.props.formatMessage({id: 'apps.allAppsAppPicker.viewYourPlan.modal.done'})}
						role='dialog'
						onClose={() => {}}
						title={AppPickerUtil.getViewYourPlanModalContent().title}
					>
						<PlanModalContent
							formatMessage={this.props.formatMessage}
							source={DOWNLOAD_CONTROLLER_USER_SOURCE.APP_PICKER_DOWNLOAD_PAGE}
						/>
					</Dialog>
				</ModalTrigger>
			</div>
		);
	}

	render() {
		return (
			<div className={DownloadPageFooterScss.footerSection} key='incompatible-os-view-footer'>
				<div className={DownloadPageFooterScss.resources}>
					<div className={DownloadPageFooterScss.heading}>
						{this.props.formatMessage({id: 'apps.appPicker.incompatibleOSView.footer.resources.heading'})}
					</div>
					{this.renderFooterList(DOWNLOAD_PAGE_FOOTER_HELPFUL_LINKS, FOOTER_LINKS_TYPE.RESOURCES)}
				</div>
				<div className={DownloadPageFooterScss.faq}>
					<div className={DownloadPageFooterScss.heading}>
						{this.props.formatMessage({id: 'apps.appPicker.incompatibleOSView.footer.faq.heading'})}
					</div>
					{this.renderFooterList(this.getFaqList(), FOOTER_LINKS_TYPE.FAQ)}
				</div>
			</div>
		);
	}
}
